import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Heart, DollarSign, FileText, MessageCircle, 
  CloudSun, AlertTriangle, CheckCircle, MapPin, Lock, Sparkles, Mail, X, Check, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cloud Button Component - Interactive trigger button shaped like a cloud
const CloudButton = ({ onClick, disabled, triggered, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || triggered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative group ${disabled || triggered ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      whileHover={!disabled && !triggered ? { scale: 1.05, y: -5 } : {}}
      whileTap={!disabled && !triggered ? { scale: 0.95 } : {}}
    >
      {/* Cloud SVG Shape */}
      <svg viewBox="0 0 200 120" className="w-48 h-28 md:w-56 md:h-32">
        <defs>
          <linearGradient id="cloud-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={triggered ? "#86efac" : isHovered ? "#B9C7D6" : "#ffffff"} />
            <stop offset="50%" stopColor={triggered ? "#4ade80" : isHovered ? "#a78bfa" : "#F7F1E8"} />
            <stop offset="100%" stopColor={triggered ? "#22c55e" : isHovered ? "#2B4C7E" : "#EBF0F5"} />
          </linearGradient>
          <filter id="cloud-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor={triggered ? "#22c55e" : "#2B4C7E"} floodOpacity="0.3"/>
          </filter>
          <filter id="cloud-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Main cloud shape */}
        <ellipse cx="100" cy="70" rx="70" ry="35" fill="url(#cloud-gradient)" filter="url(#cloud-shadow)"/>
        <ellipse cx="60" cy="55" rx="40" ry="30" fill="url(#cloud-gradient)"/>
        <ellipse cx="140" cy="55" rx="35" ry="28" fill="url(#cloud-gradient)"/>
        <ellipse cx="100" cy="45" rx="45" ry="30" fill="url(#cloud-gradient)"/>
        <ellipse cx="75" cy="40" rx="30" ry="22" fill="url(#cloud-gradient)"/>
        <ellipse cx="130" cy="42" rx="28" ry="20" fill="url(#cloud-gradient)"/>
        
        {/* Highlight */}
        <ellipse cx="85" cy="35" rx="20" ry="12" fill="white" opacity="0.4"/>
      </svg>
      
      {/* Button content overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        {triggered ? (
          <>
            <CheckCircle className="h-8 w-8 text-green-700 mb-1" />
            <span className="text-green-800 font-bold text-sm">Released</span>
          </>
        ) : (
          <>
            <CloudSun className={`h-8 w-8 mb-1 transition-colors ${isHovered ? 'text-[#1E3A63]' : 'text-[#2B4C7E]'}`} />
            <span className={`font-bold text-sm transition-colors ${isHovered ? 'text-[#182E4F]' : 'text-[#2B4C7E]'}`}>
              {children}
            </span>
          </>
        )}
      </div>
      
      {/* Sparkle effects on hover */}
      {isHovered && !triggered && !disabled && (
        <>
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ rotate: [0, 180, 360], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="h-5 w-5 text-[#3A5C8F]" />
          </motion.div>
          <motion.div
            className="absolute -bottom-1 -left-1"
            animate={{ rotate: [360, 180, 0], scale: [1, 1.3, 1] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          >
            <Sparkles className="h-4 w-4 text-[#D0B384]" />
          </motion.div>
        </>
      )}
    </motion.button>
  );
};

// Benefactor Card Component
const BenefactorCard = ({ benefactor, onTrigger, triggering }) => {
  const { 
    benefactor: user, 
    is_primary_executor, 
    is_secondary_executor,
    easy_passing_triggered,
    trigger_details,
    summary 
  } = benefactor;
  
  const canTrigger = is_primary_executor && !easy_passing_triggered;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative"
    >
      <Card className={`p-6 rounded-3xl border-2 ${
        easy_passing_triggered 
          ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50' 
          : 'border-[#D7E0EA] bg-gradient-to-br from-white via-[#EBF0F5]/30 to-[#F7F1E8]/30'
      } shadow-lg`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white ${
              easy_passing_triggered 
                ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
                : 'bg-gradient-to-br from-[#3A5C8F] to-[#C8A97E]'
            }`}>
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{user.name}</h3>
              <p className="text-gray-500 text-sm">{user.email}</p>
              <div className="flex gap-2 mt-2">
                {is_primary_executor && (
                  <span className="px-3 py-1 bg-[#EBF0F5] text-[#1E3A63] text-xs font-medium rounded-full">
                    Primary Executor
                  </span>
                )}
                {is_secondary_executor && (
                  <span className="px-3 py-1 bg-[#F2E9D9] text-[#9C7F55] text-xs font-medium rounded-full">
                    Secondary Executor
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {easy_passing_triggered && (
            <div className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Released
            </div>
          )}
        </div>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <MessageCircle className="h-6 w-6 text-[#2B4C7E] mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{summary.whispers_count}</p>
            <p className="text-xs text-gray-500">Whispers</p>
          </div>
          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <DollarSign className="h-6 w-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">${summary.total_funds.toFixed(2)}</p>
            <p className="text-xs text-gray-500">Funds</p>
          </div>
          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <FileText className="h-6 w-6 text-[#2B4C7E] mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{summary.has_will ? 'Yes' : 'No'}</p>
            <p className="text-xs text-gray-500">Will</p>
          </div>
          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <MapPin className="h-6 w-6 text-[#C8A97E] mx-auto mb-2" />
            <p className="text-lg font-bold text-gray-800 truncate">{summary.will_location || 'Not set'}</p>
            <p className="text-xs text-gray-500">Location</p>
          </div>
        </div>
        
        {/* Trigger Section for Primary Executor */}
        {is_primary_executor && (
          <div className={`p-6 rounded-2xl ${
            easy_passing_triggered 
              ? 'bg-green-100/50' 
              : 'bg-gradient-to-r from-[#EBF0F5]/50 via-[#F2E9D9]/50 to-[#EBF0F5]/50'
          }`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h4 className="font-bold text-gray-800 mb-2">
                  {easy_passing_triggered ? 'Easy Passing Completed' : 'Easy Passing Control'}
                </h4>
                <p className="text-sm text-gray-600 max-w-md">
                  {easy_passing_triggered 
                    ? `Released on ${new Date(trigger_details.triggered_at).toLocaleDateString()}. ${trigger_details.whispers_sent} whispers sent, ${trigger_details.funds_distributed} fund distributions processed.`
                    : 'As the primary executor, you have the authority to release all stored information and distribute funds when the time comes.'
                  }
                </p>
              </div>
              
              <CloudButton
                onClick={() => onTrigger(user.user_id, user.name)}
                disabled={triggering}
                triggered={easy_passing_triggered}
              >
                {triggering ? 'Processing...' : 'Release'}
              </CloudButton>
            </div>
          </div>
        )}
        
        {/* Non-executor info */}
        {!is_primary_executor && !is_secondary_executor && (
          <div className="p-4 bg-[#EBF0F5] rounded-2xl">
            <p className="text-sm text-[#1E3A63] flex items-center gap-2">
              <Heart className="h-4 w-4" />
              You are a beneficiary. The primary executor will release information when the time comes.
            </p>
          </div>
        )}
        
        {is_secondary_executor && !is_primary_executor && (
          <div className="p-4 bg-[#F7F1E8] rounded-2xl">
            <p className="text-sm text-[#9C7F55] flex items-center gap-2">
              <Lock className="h-4 w-4" />
              As secondary executor, you can trigger Easy Passing if the primary executor is unavailable.
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default function Benefactors() {
  const navigate = useNavigate();
  const [benefactors, setBenefactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null, userName: '' });
  const [pending, setPending] = useState([]);
  const [respondingId, setRespondingId] = useState(null);

  useEffect(() => {
    loadBenefactors();
    loadPending();
  }, []);

  const loadPending = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/beneficiaries/my-pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending(res.data || []);
    } catch (e) {
      console.error('Failed to load pending invitations', e);
    }
  };

  const handleRespond = async (beneficiaryId, accept) => {
    const token = localStorage.getItem('session_token');
    setRespondingId(beneficiaryId);
    try {
      await axios.post(`${BACKEND_URL}/api/beneficiaries/respond`,
        { beneficiary_id: beneficiaryId, accept },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(accept ? 'Invitation accepted 💜' : 'Invitation declined');
      setPending((p) => p.filter((x) => x.beneficiary_id !== beneficiaryId));
      if (accept) loadBenefactors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to respond');
    } finally {
      setRespondingId(null);
    }
  };

  const loadBenefactors = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/benefactors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBenefactors(response.data);
    } catch (error) {
      console.error('Failed to load benefactors:', error);
      toast.error('Failed to load benefactors');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerClick = (userId, userName) => {
    setConfirmDialog({ open: true, userId, userName });
  };

  const handleConfirmTrigger = async () => {
    const { userId } = confirmDialog;
    const token = localStorage.getItem('session_token');
    setTriggering(true);
    setConfirmDialog({ open: false, userId: null, userName: '' });

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/easy-passing/trigger/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Easy Passing initiated. ${response.data.whispers_sent} whispers released, ${response.data.funds_distributed} distributions processed.`);
      loadBenefactors();
    } catch (error) {
      console.error('Trigger failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger Easy Passing');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <motion.div 
          className="text-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <CloudSun className="h-16 w-16 text-[#2B4C7E] mx-auto mb-4" />
          <p className="text-gray-600">Loading benefactors...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
      <PageHeader title="My Benefactors" backLink="/dashboard" />

      <main className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Info Card */}
        <Card className="mb-8 p-6 rounded-3xl bg-gradient-to-r from-[#2B4C7E] via-[#C8A97E] to-[#2B4C7E] text-white shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Users className="h-7 w-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold">People Who Trust You</h2>
              <p className="text-white/80 text-sm">
                These are the people who have designated you as a beneficiary or executor of their legacy.
              </p>
            </div>
          </div>
        </Card>

        {/* Pending Invitations */}
        {pending.length > 0 && (
          <div className="mb-8" data-testid="pending-invitations">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#C8A97E]" />
              Pending Invitations
            </h3>
            <div className="space-y-3">
              {pending.map((inv) => (
                <Card key={inv.beneficiary_id} className="p-5 rounded-2xl border-2 border-[#EAD9BF] bg-gradient-to-br from-[#F7F1E8]/60 to-white shadow-md" data-testid={`pending-invite-${inv.beneficiary_id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold text-white bg-gradient-to-br from-[#3A5C8F] to-[#C8A97E] flex-shrink-0 overflow-hidden">
                        {inv.account_holder?.profile_picture
                          ? <img src={inv.account_holder.profile_picture} alt="" className="w-full h-full object-cover" />
                          : (inv.account_holder?.name?.charAt(0)?.toUpperCase() || 'U')}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{inv.account_holder?.name}</p>
                        <p className="text-sm text-gray-500">
                          invited you as <span className="font-medium text-[#836A45]">{inv.role}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        disabled={respondingId === inv.beneficiary_id}
                        onClick={() => handleRespond(inv.beneficiary_id, false)}
                        className="rounded-full border-2 hover:bg-gray-50"
                        data-testid={`pending-decline-${inv.beneficiary_id}`}
                      >
                        <X className="h-4 w-4 mr-1" /> Decline
                      </Button>
                      <Button
                        disabled={respondingId === inv.beneficiary_id}
                        onClick={() => handleRespond(inv.beneficiary_id, true)}
                        className="rounded-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white hover:from-[#1E3A63] hover:to-[#B3956B]"
                        data-testid={`pending-accept-${inv.beneficiary_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" /> Accept
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Benefactors List */}
        {benefactors.length === 0 ? (
          <Card className="p-12 text-center rounded-3xl border-2 border-dashed border-[#D7E0EA] bg-white/50">
            <CloudSun className="h-16 w-16 text-[#B9C7D6] mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">No Benefactors Yet</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              When someone adds you as a beneficiary or executor, they'll appear here. You'll be able to see what they've prepared and, if you're an executor, release their information when the time comes.
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {benefactors.map((benefactor, index) => (
              <BenefactorCard
                key={benefactor.benefactor.user_id || index}
                benefactor={benefactor}
                onTrigger={handleTriggerClick}
                triggering={triggering}
              />
            ))}
          </div>
        )}
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, userId: null, userName: '' })}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <AlertTriangle className="h-6 w-6 text-[#C8A97E]" />
              Confirm Easy Passing
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to trigger the Easy Passing process for <strong>{confirmDialog.userName}</strong>?
            </p>
            <div className="bg-[#F7F1E8] border border-[#EAD9BF] rounded-xl p-4">
              <p className="text-[#836A45] text-sm">
                <strong>This action will:</strong>
              </p>
              <ul className="text-[#9C7F55] text-sm mt-2 space-y-1">
                <li>• Release all whispers to their recipients</li>
                <li>• Distribute funds to beneficiaries</li>
                <li>• Make their information accessible</li>
                <li>• Mark their account as passed</li>
              </ul>
              <p className="text-[#836A45] text-sm mt-3 font-medium">
                This action cannot be undone.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, userId: null, userName: '' })}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTrigger}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B]"
            >
              <CloudSun className="h-4 w-4 mr-2" />
              Confirm Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
